import React from 'react';
import { Navigate } from 'react-router-dom';
import PageLoader from '../components/PageLoader';
import PrivateRoute from './PrivateRoute';
import useUserAccount from '../hooks/userAccountHook';
import { DASHBOARD_BASE } from '../resources/links';

type Props = {
  children: React.ReactNode;
};

/**
 * Route guard for pages that must only be reachable by Brolly-internal
 * staff (currently Premium Financing). Applies on top of the standard
 * auth check: insurers, brokers, agencies, financiers, and non-internal
 * users all get redirected to their dashboard overview instead of the
 * requested page. Prevents URL-typing from bypassing the sidebar filter.
 *
 * Allowed roles: Internal OWNER or Internal ADMINISTRATOR. Most Brolly-
 * internal folks work as OWNER, not ADMIN, so restricting to ADMIN-only
 * would lock the workspace owner out. Mirrors the sidebar entry's
 * `access: [OWNER, ADMINISTRATOR] + teamTypes: [INTERNAL]` in constants.ts.
 */
function InternalAdminRouteWrapper({ children }: Props) {
  const { user } = useUserAccount();

  const allowed = user?.isInternalAdmin() || user?.isInternalOwner();
  if (!allowed) {
    return <Navigate to={`${DASHBOARD_BASE}/dashboard`} replace />;
  }

  return (
    <PrivateRoute>
      <PageLoader>
        {children}
      </PageLoader>
    </PrivateRoute>
  );
}

export default InternalAdminRouteWrapper;
